import Service, { inject as service } from "@ember/service"
import { tracked } from "@glimmer/tracking"
import { A, pipe } from "@mobily/ts-belt"
import type { Planbook } from "@shared/models/planbook"
import { objectId } from "@shared/util/object-id"
import config from "cc-frontend/config/environment"
import type { Channel } from "cc-frontend/lib/phoenix"
import { Presence } from "cc-frontend/lib/phoenix"
import type EmberStore from "./ember-store"
import type Session from "./session"
import type SocketService from "./socket"

export interface PresenceData {
  id: string
  userId: string
  firstName: string
  lastName: string
  avatarColor: string
}

export default class PresenceService extends Service {
  @service declare session: Session
  @service declare socket: SocketService
  @service("ember-store") declare store: EmberStore

  // A unique ID for this tab (more specific than the user ID).
  // We don't use sessionId in case that needs to be secure.
  readonly id = objectId()

  private currentPlanbookId: string | null = null
  private channel: Channel | null = null

  // TODO: set to [] if offline (channel disconnected). Or dim or something to express uncertainty?
  @tracked
  presenceData: PresenceData[] = []

  constructor() {
    super(...arguments)

    this.session.addObserver("token", () => this.tryConnect())
    this.session.addObserver("isImpersonating", () => this.tryConnect())
    // TODO: send current presence state when the channel reconnects (will happen automatically).
  }

  get presenceDataExclUser(): PresenceData[] {
    return this.presenceData.filter((data) => data.userId !== this.session.userId)
  }

  setPlanbookId(planbookId: string | null): void {
    if (planbookId === this.currentPlanbookId) return

    if (this.channel) {
      console.log(`Leaving channel ${this.channel.topic}`)
      this.channel.leave()
      this.channel = null
    }

    this.currentPlanbookId = planbookId
    this.tryConnect()
  }

  private async tryConnect() {
    if (config.environment === "test") return
    if (this.channel) return
    if (!this.currentPlanbookId) return

    const currentPlanbook: Planbook | null = await this.store.find(
      "planbook",
      this.currentPlanbookId
    )
    if (!currentPlanbook || currentPlanbook.id !== this.currentPlanbookId) return

    // TODO: Add an observer on permissions that calls tryConnect?
    if (!currentPlanbook.meta.permissions.includes("can-share-presence")) {
      return
    }
    if (!this.session.token) return
    if (this.session.isImpersonating) {
      // Don't frighten users by showing them support users who are impersonating.
      return
    }

    if (!this.socket.socket) {
      this.socket.connect()
      if (!this.socket.socket) {
        console.log("Socket not connected")
        return
      }
    }

    console.log(`Joining channel planbook-presence:${this.currentPlanbookId}`)
    this.channel = this.socket.socket.channel(`planbook-presence:${this.currentPlanbookId}`, {
      id: this.id,
      token: this.session.token,
    })
    this.channel.on
    this.channel.onError((error) => console.error("Presence channel error", error))
    const presence = new Presence(this.channel)
    presence.onSync(() => {
      this.presenceData = pipe(
        presence.list() as unknown as Array<{ metas: Array<PresenceData> }>,
        // There should be one presence entry per channel connection (= tab),
        // but if not, extract the last one.
        A.filter((entry) => entry.metas.length > 0),
        A.map((entry) => entry.metas[entry.metas.length - 1]),
        A.sortBy((data) => `${data.lastName} ${data.firstName} ${data.id}`)
      )
    })

    this.channel
      .join()
      .receive("ok", () => {
        console.log("Planbook presence channel joined successfully")
      })
      .receive("error", (error) => {
        console.error("Presence channel join error", error)
      })
      .receive("timeout", () => {
        console.error("Presence channel join timeout")
      })
  }
}
